import { useAuth0 } from '@auth0/auth0-react';
import { createContext, useContext, useEffect, useState } from 'react';

import { fetchCurrentStaff, type CurrentStaff as CurrentUser } from '../api/staff';

interface CurrentUserContextValue {
  currentUser: CurrentUser | null;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * Two attempts, never more. The first `/staff/me` of a cold load can fail for
 * reasons that are gone a tick later, and a profile failure is not cosmetic:
 * it strips a Regional Director of their regions, which is what the chrome and
 * the landing redirects are scoped by. An earlier cut of BON-558 was reverted
 * for a retry that did not stop, so this one is bounded by having no loop in
 * it at all.
 *
 * `loading` deliberately stays true across BOTH attempts, so no consumer can
 * observe the intermediate failure and act on it: once `loading` is false, an
 * `error` means the profile is genuinely unavailable.
 */
async function fetchCurrentStaffOnceMore(): Promise<CurrentUser> {
  try {
    return await fetchCurrentStaff();
  } catch {
    return await fetchCurrentStaff();
  }
}

const CurrentUserContext = createContext<CurrentUserContextValue>({
  currentUser: null,
  // Matches the provider's own initial state below — see the comment there
  // for why `false` here would be wrong.
  loading: true,
  error: null,
  refresh: () => {},
});

export function CurrentUserProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  // Starts true, not false. This provider always mounts behind
  // ProtectedRoute, which already held for Auth0 to settle — so the very
  // first render here is "about to fetch", never "fetched, no user". Getting
  // this wrong reads as "loaded, no user" on first paint, which is exactly
  // what used to send HomeRedirect down the wrong path before useAppMode's
  // own loading guard papered over it (BON-558).
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const user = await fetchCurrentStaffOnceMore();
      setCurrentUser(user);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load user profile'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    // Defensive: this provider is only ever mounted inside ProtectedRoute, so
    // in practice `isAuthenticated` is already true by the time we get here.
    // Guarding it anyway means an unauthenticated mount resolves to "not
    // loading" instead of spinning forever.
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    load();
  }, [isAuthenticated, authLoading]);

  return (
    <CurrentUserContext.Provider value={{ currentUser, loading, error, refresh: load }}>
      {children}
    </CurrentUserContext.Provider>
  );
}

export function useCurrentUser() {
  return useContext(CurrentUserContext);
}
